import React, { useCallback, useEffect, useState } from 'react';
import { api, brl, dateStr } from '../lib/api';
import { toast } from 'sonner';
import { Ban, X } from 'lucide-react';

const PERIODS = [{ id: 'today', label: 'Hoje' }, { id: 'week', label: 'Semana' }, { id: 'month', label: 'Mês' }, { id: 'year', label: 'Ano' }];
const TABS = [{ id: 'sales', label: 'Vendas' }, { id: 'products', label: 'Produtos' }, { id: 'employees', label: 'Funcionários' }];

export default function Reports() {
  const [tab, setTab] = useState('sales');
  const [period, setPeriod] = useState('month');
  const [sales, setSales] = useState([]);
  const [prods, setProds] = useState([]);
  const [emps, setEmps] = useState([]);
  const [cancelSale, setCancelSale] = useState(null);
  const [reason, setReason] = useState('');

  const load = useCallback(async () => {
    const [s, p, e] = await Promise.all([
      api.get('/sales?limit=500'),
      api.get(`/reports/products?period=${period}`),
      api.get(`/reports/employees?period=${period}`),
    ]);
    setSales(s.data); setProds(p.data); setEmps(e.data);
  }, [period]);
  useEffect(() => { load(); }, [load]);

  const doCancel = async () => {
    if (!reason.trim()) { toast.error('Motivo obrigatório'); return; }
    try {
      await api.post(`/sales/${cancelSale.id}/cancel`, { reason });
      toast.success('Venda cancelada · estoque restaurado');
      setCancelSale(null); setReason(''); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro'); }
  };

  return (
    <div className="p-4 h-full overflow-y-auto space-y-3">
      <h2 className="text-xl font-black">Relatórios</h2>
      <div className="flex gap-2 overflow-x-auto">
        {PERIODS.map((p) => (
          <button key={p.id} onClick={() => setPeriod(p.id)} data-testid={`rep-period-${p.id}`}
            className={`h-9 px-4 rounded-full text-sm font-bold whitespace-nowrap ${period === p.id ? 'bg-orange-500 text-white' : 'bg-white border border-slate-200 text-slate-600'}`}>{p.label}</button>
        ))}
      </div>
      <div className="flex gap-2">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`rep-tab-${t.id}`}
            className={`flex-1 h-10 rounded-xl text-sm font-bold ${tab === t.id ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200'}`}>{t.label}</button>
        ))}
      </div>

      {tab === 'sales' && (
        <div className="space-y-2">
          {sales.map((s) => (
            <div key={s.id} className={`bg-white rounded-xl border ${s.cancelled ? 'border-red-200 bg-red-50/30' : 'border-slate-200'} p-3 text-sm`} data-testid={`sale-row-${s.id}`}>
              <div className="flex justify-between">
                <div>
                  <div className="font-bold">#{s.id.slice(0, 8)} · {dateStr(s.timestamp)}</div>
                  <div className="text-xs text-slate-500">{s.username} · {s.payment_method} · {s.items.length} itens</div>
                  {s.cancelled && <div className="text-xs text-red-600 font-bold">CANCELADA: {s.cancel_reason}</div>}
                </div>
                <div className="text-right">
                  <div className={`font-black ${s.cancelled ? 'line-through text-slate-400' : 'text-orange-600'}`}>{brl(s.total)}</div>
                  <div className="text-xs text-green-600">Lucro {brl(s.profit)}</div>
                </div>
              </div>
              {!s.cancelled && (
                <button onClick={() => setCancelSale(s)} data-testid={`sale-cancel-${s.id}`} className="mt-2 text-xs text-red-600 font-bold flex items-center gap-1"><Ban size={12}/> Cancelar venda</button>
              )}
            </div>
          ))}
          {sales.length === 0 && <div className="text-slate-500 text-sm">Nenhuma venda</div>}
        </div>
      )}

      {tab === 'products' && (
        <div className="space-y-1">
          {prods.map((p) => (
            <div key={p.product_id} className="bg-white rounded-lg border border-slate-100 p-2 text-sm flex justify-between">
              <div><div className="font-semibold">{p.product_name}</div><div className="text-xs text-slate-500">{p.quantity} un · Custo {brl(p.cost)}</div></div>
              <div className="text-right"><div className="font-black text-orange-600">{brl(p.revenue)}</div><div className="text-xs text-green-600">Lucro {brl(p.profit)}</div></div>
            </div>
          ))}
          {prods.length === 0 && <div className="text-slate-500 text-sm">Sem dados</div>}
        </div>
      )}

      {tab === 'employees' && (
        <div className="space-y-1">
          {emps.map((e) => (
            <div key={e.user_id} className="bg-white rounded-lg border border-slate-100 p-2 text-sm flex justify-between">
              <div><div className="font-semibold">{e.name}</div><div className="text-xs text-slate-500">{e.num_sales} vendas · Comissão {e.commission_pct}%</div></div>
              <div className="text-right"><div className="font-black text-orange-600">{brl(e.revenue)}</div><div className="text-xs text-blue-600">Comissão {brl(e.commission)}</div></div>
            </div>
          ))}
          {emps.length === 0 && <div className="text-slate-500 text-sm">Sem dados</div>}
        </div>
      )}

      {cancelSale && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center sm:justify-center" onClick={() => setCancelSale(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-4 space-y-3">
            <div className="flex justify-between"><div className="font-black">Cancelar venda #{cancelSale.id.slice(0,8)}</div><button onClick={() => setCancelSale(null)}><X/></button></div>
            <input placeholder="Motivo do cancelamento" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="cancel-reason" className="w-full h-11 rounded-xl border border-slate-200 px-3"/>
            <button onClick={doCancel} data-testid="cancel-confirm" className="w-full h-12 rounded-xl bg-red-500 text-white font-black">Confirmar cancelamento</button>
          </div>
        </div>
      )}
    </div>
  );
}
